import  React from 'react'
import Button from 'react-bootstrap/Button'
import { ClaimRelationship } from '../../../../models/ClaimRelationship'
import {
  TextField, BooleanRadioButtonField, yesNoDataSource, TextSelectField, validateFields, AddressField,
} from '../../../../shared/fields'
import { TableColumn, ISection } from '../../../../shared/table'
import { ShowIf } from '../../../../shared/fields/ShowIf'
import { ISupportSpinner } from '../../../../shared/Ajax'
import { newGuid, getAddressString } from '../../../../shared/Utility'
import { Backdrop } from '../../../../shared/Backdrop'
import { ClaimModel } from '../../../../models/ClaimModel'
import { Option } from '../../../../models/Option'

export const relationshipColumns: TableColumn<ClaimRelationship>[] = [
  { name: 'Company name', content: (d) => d.companyName },
  { name: 'Relationship', content: (d) => d.relationship },
  { name: 'Nature of business activity', content: (d) => d.activityText, printOnly: true },
  { name: 'ACN', content: (d) => d.acn, printOnly: true },
  { name: 'Address', content: (d) => getAddressString(d.registeredAddress), printOnly: true },
  { name: 'Applying for EMDG', content: (d) => (d.applyingForEMDG ? 'Yes' : 'No') },
]

export interface RelationshipDetailsProps {
    id: string
    model: ClaimRelationship
    onChange: { (v: ClaimRelationship, close: boolean): any }
    onClose: { (): any }
    onRemove: { (): any }
    importing: boolean
    preview?: boolean
    claim: ClaimModel
    addressCountries: Option<string>[]
}

export interface RelationshipDetailsState {
    model: ClaimRelationship
    loading: boolean
}

export class RelationshipDetails extends React.Component<RelationshipDetailsProps, RelationshipDetailsState> implements ISection, ISupportSpinner {
    companyName: TextField
    activityText: TextField
    acn: TextField
    registeredAddress: AddressField
    applyingForEMDG: BooleanRadioButtonField
    relationship: TextSelectField
    otherRelationship: TextField

    constructor(props: RelationshipDetailsProps) {
      super(props)

      const model = this.props.model ? { ...this.props.model } : {
        id: newGuid(),
        companyName: '',
        activityText: '',
        acn: '',
        registeredAddress: null,
        applyingForEMDG: null,
        relationship: null,
        invalid: false,
      }

      this.state = {
        model,
        loading: false,
      }
    }

    toggleSpinner(on: boolean, callback: { (): any }): void {
      this.setState({ loading: on }, callback)
    }

    validate = (callback: { (res: boolean): any }) => {
      validateFields((res) => this.onChange({ invalid: !res }, () => callback(res)),
        this.companyName,
        this.activityText,
        this.acn,
        this.registeredAddress,
        this.applyingForEMDG,
        this.relationship,
        this.otherRelationship)
    }

    saveAndClose = (callback?: { (res: boolean): any }) => {
      if (this.state.loading) {
        callback && callback(false)
        return
      }
      this.validate((res) => {
        if (res) {
          this.props.onChange(this.state.model, true)
        }
        callback && callback(res)
      })
    }

    componentDidMount() {
      if (this.props.importing) {
        this.validate((res) => {
          const temp = { invalid: !res }
          this.props.onChange({ ...this.state.model, ...temp }, true)
        })
      } else if (this.state.model.invalid) {
        this.validate(() => { })
      }
    }

    onChange = (change: Partial<ClaimRelationship>, callback?: {():any}) => {
      this.setState({
        model: { ...this.state.model, ...change },
      }, callback)
    }

    isSameAcnAsApplicant(acn: string): boolean {
      return acn && this.props.claim.acn && this.props.claim.acn.replace(/\s/g, '') == acn.replace(/\s/g, '')
    }

    render() {
      const relationshipOriginalDataSource = [{ value: 'Parent', label: 'Parent' }, { value: 'Stakeholder', label: 'Stakeholder' }]
      const relationshipDataSource = [
        { value: 'ParentCompany', label: 'Parent company' },
        { value: 'SubsidiaryCompany', label: 'Subsidiary company' },
        { value: 'SubsidiaryWithSameParentCompany', label: 'Subsidiary with same parent company' },
        { value: 'Shareholder', label: 'Shareholder' }]

      const { relationship } = this.state.model
      const originalRelationship = relationshipOriginalDataSource.filter((r) => r.value === relationship)

      if (relationship && originalRelationship.length > 0) {
        relationshipDataSource.push({ value: originalRelationship[0].value, label: originalRelationship[0].label })
      }

      const { model } = this.state

      return (
        <>
          <TextField
            ref={(r) => this.companyName = r}
            id={`${this.props.id}-companyName`}
            label="Company name"
            value={model.companyName}
            onChange={(companyName) => this.onChange({ companyName })}
            required
            maxLength={80}
            preview={this.props.preview}
          />
          <TextField
            ref={(r) => this.activityText = r}
            id={`${this.props.id}-activityText`}
            label="Nature of business activity"
            value={model.activityText}
            onChange={(activityText) => this.onChange({ activityText })}
            required
            maxLength={80}
            preview={this.props.preview}
          />
          <TextField
            ref={(r) => this.acn = r}
            id={`${this.props.id}-acn`}
            label="ACN"
            value={model.acn}
            onChange={(acn) => this.onChange({ acn })}
            maxLength={9}
            block={false}
            preview={this.props.preview}
            errorMessage={this.isSameAcnAsApplicant(model.acn) ? "Australian Company Number cannot be the same as applicant's ACN" : ''}
          />
          <AddressField
            ref={(r) => this.registeredAddress = r}
            id={`${this.props.id}-registeredAddress`}
            label="Registered address"
            value={model.registeredAddress}
            onChange={(registeredAddress) => this.onChange({ registeredAddress })}
            required
            preview={this.props.preview}
            countries={this.props.addressCountries}
          />
          <TextSelectField
            ref={(r) => this.relationship = r}
            id={`${this.props.id}-relationship`}
            label="Relationship"
            value={relationship}
            onChange={(relationship) => this.onChange({ relationship })}
            dataSource={relationshipDataSource}
            required
            preview={this.props.preview}
          />
          <TextField
            ref={(r) => this.otherRelationship = r}
            id={`${this.props.id}-otherRelationship`}
            label="Other relationship"
            value={model.relationship == 'Other' ? '' : model.relationship}
            onChange={(relationship) => this.onChange({ relationship })}
            maxLength={80}
            visible={relationship == 'Other'}
            required={relationship == 'Other'}
            preview={this.props.preview}
          />
          <BooleanRadioButtonField
            ref={(r) => this.applyingForEMDG = r}
            id={`${this.props.id}-applyingForEMDG`}
            label="Is this company applying for EMDG?"
            value={model.applyingForEMDG}
            onChange={(applyingForEMDG) => this.onChange({ applyingForEMDG })}
            dataSource={yesNoDataSource}
            required
            preview={this.props.preview}
          />
          <ShowIf condition={!this.props.preview} className="clearfix mt-3">
            <Button className="emdg-btn float-right" onClick={(e) => this.saveAndClose()}>Complete row</Button>
            <Button  className="emdg-btn emdg-btn--secondary float-left mr-2" onClick={this.props.onClose}>Cancel</Button>
            <Button  className="emdg-btn emdg-btn--secondary float-left" onClick={this.props.onRemove} style={{ display: this.props.model ? '' : 'none' }}>Remove</Button>
          </ShowIf>

          <Backdrop on={this.state.loading} />
        </>
      )
    }
}
